<script setup>
import {watch, computed, ref} from 'vue';
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import ButtonGroup from "primevue/buttongroup";
import { useServerTable } from '@/composables/useServerTable.js';
import { useAttachmentStore } from '@/stores/attachment.js';
import DocumentForm from './DocumentForm.vue';
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import { useToast } from "primevue/usetoast";

const props = defineProps({
  target: { type: String, required: true },
  entityId: { type: [Number, String], required: true },
});

const store = useAttachmentStore();
const showAddDialog = ref(false);
const showConfirmDeleteDialog = ref(false);
const documentToDelete = ref(null);
const toast = useToast();
const availableDocTypes = ref([]);

const {
  data: documents,
  totalRecords,
  loading,
  rowsPerPage,
  onPageChange,
  setSearch,
  setExtra,
  firstIndex,
  load,
} = useServerTable({
  initialRows: 5,
  debounceMs: 500,
  loadPage: params => store.loadDocuments(params),
});

// Следим за props и обновляем параметры для useServerTable
watch(
    () => [props.target, props.entityId],
    async ([target, id]) => {
      if (target && id != null) {
        setExtra({ target, id });
        // Загружаем доступные типы документов
        try {
          availableDocTypes.value = await store.fetchDocumentTypes({ target, kind: 'document' });
        } catch (e) {
          availableDocTypes.value = [];
        }
      }
    },
    { immediate: true }
);

const showControls = computed(() => loading.value || totalRecords.value > 0);
const canAddDocuments = computed(() => availableDocTypes.value.length > 0);

function downloadDocument(url) {
  if (url) {
    window.open(url, '_blank');
  }
}
function openDeleteDialog(doc) {
  documentToDelete.value = doc;
  showConfirmDeleteDialog.value = true;
}

async function handleConfirmDelete() {
  if (!documentToDelete.value) return;
  try {
    await store.deleteAttachment(documentToDelete.value.id);
    toast.add({ severity: 'success', summary: 'Успех', detail: 'Документ удален', life: 2500 });
    await load(); // Обновляем список
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить документ', life: 4000 });
  } finally {
    showConfirmDeleteDialog.value = false;
    documentToDelete.value = null;
  }
}
</script>

<template>
  <div>
    <Card>
      <template #title>
        <div class="w-full flex items-center justify-between gap-3 px-4 text-lg">
          <span>Документы</span>
          <div class="flex items-center gap-2">
            <IconField v-if="showControls">
              <InputIcon class="pi pi-search" />
              <InputText placeholder="Поиск документов..." size="small" class="h-7" @update:modelValue="setSearch"/>
            </IconField>
            <Button v-if="canAddDocuments" icon="pi pi-plus" rounded text @click="showAddDialog = true" size="small"/>
          </div>
        </div>
      </template>
      <template #content>
        <DataTable
            :value="documents"
            :paginator="totalRecords > rowsPerPage"
            :rows="rowsPerPage"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPageChange"
            :first="firstIndex()"
            lazy
            responsive-layout="scroll"
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate="от {first} до {last} из {totalRecords}"
            emptyMessage="Документов нет"
            class="text-sm"
        >
          <Column field="file_name">
            <template #body="{ data }">
              <span class="truncate">
                {{ data.file_name }}
              </span>
            </template>
          </Column>
          <Column style="width: 80px;">
            <template #body="{ data }">
              <ButtonGroup>
                <Button
                    icon="pi pi-download"
                    size="small"
                    severity="secondary"
                    rounded
                    text
                    @click="downloadDocument(data.download_url)"
                />
                <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="warn"
                    rounded
                    text
                    @click="openDeleteDialog(data)"
                />
              </ButtonGroup>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
    <DocumentForm
        v-model:visible="showAddDialog"
        :target="props.target"
        :entity-id="props.entityId"
        @saved="load"
    />
    <ConfirmDeleteDialog
        v-model:visible="showConfirmDeleteDialog"
        :documentName="documentToDelete?.file_name"
        @confirm="handleConfirmDelete"
    />
  </div>
</template>

<style scoped>
:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  border: none !important;
  background: transparent !important;
  border-bottom: none !important;
}
:deep(.p-datatable-tbody > tr > td) {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}
:deep(.p-datatable-thead > tr > th) {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}
:deep(.p-card-body) {
  padding: 2px 0 8px;
}
</style>
